import {v3} from "oc-threejs/short3";
import Brett from "./Brett";
import Commons from "oc-commons/Commons";
import Maths from "oc-commons/Maths";
import FullHeightDragable from "./FullHeightDragable";
import Element from "./Element";
import Slopings from "./Slopings";
import type {HasLocalCoordinates} from "./HasLocalCoordinates";

/**
 * Grob gesehen ist eine Trennwandbox ein Schrank im Schrank.
 * Sie ist ein durch Konstruktionsböden/Deckel/Boden begrenztes Interieur,
 * das durch n (n>=1) Trennwände horizontal unterteilt ist. Durch die Trennwände wird
 * die Trennwandbox in n+1 Elemente unterteilt. Dadurch entsteht eine rekursive Struktur.
 * Eine Trennwandbox verhält sich vom Dragging ähnlich wie eine Tür:
 * Es gibt oben und unten Draghandles, und die Box kann als ganzes verschoben werden.
 */
export default class Trennwandbox extends FullHeightDragable {

    // persistent (UserLayer) ------------------------------------------

    elements = [];

    // transient (CompiledLayer) ---------------------------------------

    elementsBox = new THREE.Box3();

    trennwaende: Brett[] = [];

    slopings: Slopings;
    slopeX = 0;

    constructor(type: string, element: ?Object) {
        super(type, element);
        this.setNumberOfElements(2);
    }

    get schrankboden() {
        return this.boden.bottom;
    }

    get angleSide() {
        return this.schrank.angleSide;
    }

    get element() {
        return this.element_;
    }

    set element(v) {
        if (this.elements && this.elements.includes(v)) {
            throw 'attempt to add Trennwandbox to its own element!';
        }
        return this.element_ = v;
    }

    createElement() {
        const element = new Element(this);
        return element;
    }

    setNumberOfElements(count) {
        Commons.setArrayLength(this.elements, count, () => this.createElement());
    }

    fromJSON(json) {
        super.fromJSON(json);
        this.elements = Commons.mapreuse(json.elements,this.elements,e => this.createElement(),(d,s)=>d.fromJSON(s));
        this.isDropped = true;
        return this;
    }

    toJSON() {
        const json = super.toJSON();
        json.elements = this.elements.map(e => e.toJSON());
        return json;
    }

    copy(trennwandbox,createInterceptor) {
        super.copy(trennwandbox,createInterceptor);
        this.elements = Commons.mapreuse(trennwandbox.elements,this.elements,e => this.createElement(),(d,s)=>d.copy(s,createInterceptor));
        return this;
    }

    compileInternals() {
        super.compileInternals();
        this._compileTrennwaende();
        this._compileElements();
        this.elements.forEach(element => element.compileParts());
        // this._compileFrontCounts();
        this.elements.forEach(element => element.compileFronts());
    }

    _compileBox() {
        super._compileBox();
        this._compileSlopings();
        this._compileElementSizeAndBox();
        this._compileElementDeckel();
    }

    setElementBreiten(breiten) {
        breiten.forEach((breite,index)=>{
            const element = this.elements[index];
            if (!element) {
                return;
            }
            element.locked.x = true;
            element.size.x = breite;
        });
    }

    _compileElementSizeAndBox() {
        this.elementsBox = new THREE.Box3(v3(), this.box.getSize());
        this.frontBox = this.elementsBox.clone();

        const brett_staerke = APP.settings.getFloat('brett_staerke');
        const numberOfInnenwaende = this.elements.length - 1;
        const wallSpace = numberOfInnenwaende * brett_staerke;

        const lockedCount = () => this.elements.reduce((acc, e) => acc + (e.locked.x ? 1 : 0), 0);
        const lockedSpace = () => this.elements.reduce((acc, e) => acc + (e.locked.x ? e.size.x : 0), 0);
        const freeCount = () => this.elements.reduce((acc, e) => acc + (e.locked.x ? 0 : 1), 0);
        const freeSpace = () => this.size.x - wallSpace - lockedSpace();
        const usedSpace = () => wallSpace + this.elements.reduce((acc, e) => acc + e.size.x, 0);
        const spaceDiff = () => Math.abs(usedSpace() - this.size.x);
        const epsilon = 0.001;

        this.elements.forEach(e=>{
            e.box.copy(this.elementsBox);
            e.setSize(this.size);
        });

        while (true) {
            const spacePerFreeElement = freeSpace() / freeCount();
            this.elements.filter(e => !e.locked.x).forEach(e => e.size.x = spacePerFreeElement);
            if (lockedCount() == 0 || spaceDiff() < epsilon) {
                break;
            } else {
                _.last(this.elements).locked.x = false;
            }
        }

        this.elements.reduce((acc, e) => {
            e.box.min.x = acc;
            e.compileBox(e.box.min.x);
            return e.box.max.x + brett_staerke;
        }, 0);
    }

    _compileElementDeckel() {
        if (!this.element) {
            return;
        }
        const brett_staerke = APP.settings.getFloat('brett_staerke');
        this.elements.forEach(e => {
            const elementSchrankBox = e.schrankBox.clone();
            elementSchrankBox.max.y += brett_staerke;
            elementSchrankBox.min.z -= brett_staerke*2;

            e.deckel = this.element.deckel.filter(d => elementSchrankBox.intersectsBox(d.box));
            if (e.deckel.length == 0 && this.boden.top) {
                e.deckel.push(this.boden.top);
            }

            e.rueckwand = this.element.rueckwand.filter(r => elementSchrankBox.intersectsBox(r.box));
        });
    }

    _compileSlopings() {
        this.slopeX = Maths.cutBetween(0, this.schrank.slopeX - this.box.min.x, this.size.x);
        const brett_staerke = APP.settings.getFloat('brett_staerke');
        this.slopings = new Slopings(this.size.y+brett_staerke, this.elementsBox.min.z);
        if (this.schrank.slopings.isSloped(this.box, true)) {
            const yOfX = x => Maths.cutBetween(0, this.schrank.slopings.yOfX(x + this.box.min.x, 0) - this.y, this.size.y);
            if (this.angleSide === 'left') {
                this.slopings.sx = new Slopings.Sloping(0, yOfX(0), this.slopeX, yOfX(this.slopeX));
            } else if (this.angleSide === 'right') {
                this.slopings.sx = new Slopings.Sloping(this.slopeX, yOfX(this.slopeX), this.size.x, yOfX(this.size.x));
            } else if (this.angleSide === 'back') {
                const yOfZ = z => Maths.cutBetween(0, this.schrank.slopings.yOfZ(z + this.box.min.z, 0) - this.y, this.size.y);
                const zMax = Maths.cutBetween(0, this.schrank.slopings.zOfY(this.box.max.y, 0), this.size.z);
                this.slopings.sz = new Slopings.Sloping(0, yOfZ(0), zMax, this.size.y);
            }
        }
    }

    _compileTrennwaende() {
        const numberOfInnenwaende = this.elements.length - 1;

        const lazyTrennwaende = APP.model.lazyPool(this, 'trennwaende', () => {
            const brett = new Brett(this, 'Trennwand', 'yz');
            brett.isInner = true;
            return brett;
        });
        const innerWallBox = this.elementsBox.clone();

        for (let i = 0; i < numberOfInnenwaende; i++) {
            const element = this.elements[i];
            const nextElement = this.elements[i + 1];
            const wall = lazyTrennwaende.next();
            wall.box = innerWallBox.clone();

            wall.box.min.x = element.box.max.x;
            wall.box.max.x = nextElement.box.min.x;

            element.wallRight = wall;
            nextElement.wallLeft = wall;
            this._wandGrater(wall);
        }
        lazyTrennwaende.cleanup();
        this.trennwaende.forEach(wall => wall.compile());
    }

    _compileElements() {
        if (this.element) {
            _.first(this.elements).wallLeft = this.element.wallLeft;
            _.last(this.elements).wallRight = this.element.wallRight;
        }
        this.trennwaende.forEach((wall,i) => {
            if (i<this.trennwaende.length-1) {
                this.elements[i+1].wallLeft = wall;
            }
            this.elements[i].wallRight = wall;
        });
    }

    setHeightToElementMaximum() {
    }

    _wandGrater(wall) {
        wall.boxToVertices();
        if (this.angleSide === 'back') {
            wall.xEdgeZY(0, 1).z = wall.box.max.z - this.sizeTop.z;
            wall.xEdgeZY(2, 1)._ = {y: this.sizeTop.y, z: wall.box.min.z};
            wall.updateBox();
        } else if (this.slopings && this.slopings.isSloped(wall.box, true)) {
            wall.zEdgeXY(0, 1).y = this.slopings.yOfX(wall.box.min.x, 0);
            wall.zEdgeXY(1, 1).y = this.slopings.yOfX(wall.box.max.x, 0);
            wall.updateBox();
        }
    }

    iterateElements(iterator) {
        this.elements.forEach(e=>e.iterateElements(iterator));
    }


    acceptElement(element) {
        return super.acceptElement(element) && !element.isChildOf(this);
    }

    iterate(iterator) {
        iterator(this);
        this.elements.forEach(x=>x.iterate(iterator));
        this.trennwaende.forEach(x=>x.iterate(iterator));
    }

}
